import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Pencil, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/badge';
import { Alert } from '@/components/ui/Alert';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { CollapseSection, DetailKV, Section } from '@/components/ui/DetailSection';
import { SOLUTION_CATALOG_PATH } from '@/features/database/solution/paths';
import { AppearancePreview } from './AppearancePreview';
import {
  conflictExtra,
  isConflict,
  toastUnlessConflict,
  useAppearanceList,
  usePaletteItem,
  usePatchPaletteItem,
  useTogglePaletteItem,
} from '../api';
import type { Library, PaletteKind } from '../types';

const LABELS: Record<PaletteKind, { noun: string; catalog: string }> = {
  pattern: { noun: 'dezén', catalog: SOLUTION_CATALOG_PATH },
  finish: { noun: 'úpravu povrchu', catalog: SOLUTION_CATALOG_PATH },
};

export function PaletteItemDetailPanel({
  kind,
  id,
  modelId,
  canEdit,
  onNavigate,
  onBack,
}: {
  kind: PaletteKind;
  id: string;
  modelId: string;
  canEdit: boolean;
  onNavigate: (library: Library, id: string) => void;
  onBack: () => void;
}) {
  const { data: item, isLoading, isError } = usePaletteItem(kind, id);
  const usedIn = useAppearanceList(modelId, {
    isActive: 'true,false',
    ...(kind === 'pattern' ? { modelPattern: id } : { modelFinish: id }),
  });
  const rename = usePatchPaletteItem(kind);
  const toggle = useTogglePaletteItem(kind);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [blocking, setBlocking] = useState<{ id: string; title: string }[]>([]);

  if (isLoading) return <SpinnerOverlay />;
  if (isError || !item)
    return (
      <EmptyState
        title="Položku palety se nepodařilo načíst"
        description="Zkuste ji vybrat znovu."
      />
    );

  const saveTitle = async (customTitle: string | null) => {
    await rename.mutateAsync({ id: item.id, customTitle });
    setEditing(false);
    toast.success('Název položky palety byl uložen');
  };

  const setActive = async (active: boolean) => {
    try {
      await toggle.mutateAsync({ id: item.id, active });
      setBlocking([]);
      toast.success(active ? `${item.code} vrácen do palety` : `${item.code} odebrán z palety`);
    } catch (error) {
      if (isConflict(error)) {
        const appearances = conflictExtra(error).appearances ?? [];
        if (appearances.length) setBlocking(appearances);
        else toast.error('Stejné řešení je v paletě už aktivní jako jiná položka.');
      }
      toastUnlessConflict(error);
    }
  };

  const solution = item.classified_solution;

  return (
    <article className="h-full overflow-y-auto" aria-label="Detail položky palety">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-150 px-5 py-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            aria-label="Zpět na seznam"
            onClick={onBack}
          >
            <ArrowLeft />
          </Button>
          <h2 className="text-sm font-semibold">Detail položky palety</h2>
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={toggle.isLoading}
              onClick={() => setActive(!item.is_active)}
            >
              <Power />
              {item.is_active ? 'Odebrat z palety' : 'Vrátit do palety'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setTitle(item.custom_title ?? '');
                setEditing(true);
              }}
            >
              <Pencil />
              Přejmenovat
            </Button>
          </div>
        )}
      </div>
      <div className="px-5 py-5">
        {blocking.length > 0 && (
          <Alert variant="warning" title="Položku nelze odebrat z palety" className="mb-4">
            <p>
              Používají ji tyto aktivní vzhledy. Nejdřív {LABELS[kind].noun} ze vzhledů odeberte.
            </p>
            <ul className="mt-2 space-y-1">
              {blocking.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    className="text-primary-700 hover:underline"
                    onClick={() => onNavigate('appearances', entry.id)}
                  >
                    {entry.title}
                  </button>
                </li>
              ))}
            </ul>
          </Alert>
        )}
        <AppearancePreview
          patternImageUrl={solution.image_url}
          label={item.title}
          className="h-44 w-full"
        />
        <div className="mt-5 mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-neutral-500">{item.code}</span>
            <h2 className="text-xl font-semibold text-neutral-900 break-words">{item.title}</h2>
            {!item.is_active && <Badge variant="warning">Odebráno z palety</Badge>}
          </div>
        </div>
        {!solution.is_active && (
          <Alert variant="warning" className="mb-4">
            Řešení bylo v katalogu deaktivováno. Položka palety i vzhledy fungují dál.
          </Alert>
        )}
        {editing && (
          <form
            className="flex flex-wrap items-center gap-2 mb-4"
            onSubmit={(event) => {
              event.preventDefault();
              saveTitle(title.trim() || null).catch(() => undefined);
            }}
          >
            <Input
              aria-label="Vlastní název položky"
              autoFocus
              className="flex-1 min-w-[200px]"
              value={title}
              maxLength={255}
              placeholder={solution.title || item.code}
              onChange={(event) => setTitle(event.target.value)}
            />
            <Button type="submit" size="sm" disabled={rename.isLoading}>
              Uložit
            </Button>
            {item.custom_title && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => saveTitle(null).catch(() => undefined)}
              >
                Zrušit vlastní název
              </Button>
            )}
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Storno
            </Button>
          </form>
        )}
        <Section title="Katalog">
          <DetailKV label="Vlastní název" value={item.custom_title || 'Nezadán'} />
          <DetailKV label="Název v katalogu" value={solution.title || 'Bez schválené verze'} />
          <DetailKV
            label="Řešení"
            value={
              <Link
                to={`${LABELS[kind].catalog}${solution.solution_id}`}
                className="inline-flex items-center gap-1 text-primary-700 hover:underline"
              >
                Otevřít v katalogu <ExternalLink size={12} />
              </Link>
            }
          />
        </Section>
        <CollapseSection title="Použito ve vzhledech" badge={usedIn.count} defaultOpen>
          {usedIn.items.length ? (
            <div className="flex flex-col items-start gap-1">
              {usedIn.items.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="text-left text-sm text-primary-600 hover:underline py-1"
                  onClick={() => onNavigate('appearances', entry.id)}
                >
                  {entry.title}
                  {!entry.is_active && <span className="text-neutral-400"> · neaktivní</span>}
                </button>
              ))}
              {usedIn.hasNextPage && (
                <Button variant="link" size="sm" onClick={() => usedIn.fetchNextPage()}>
                  Načíst další
                </Button>
              )}
            </div>
          ) : (
            <p className="text-xs text-neutral-500">Zatím nepoužito v žádném vzhledu.</p>
          )}
        </CollapseSection>
      </div>
    </article>
  );
}
